/*
 * color-swatch-nav.js — Navegación AJAX entre colores en la ficha de producto (PDP).
 *
 * En Kokonote cada color es un PRODUCTO independiente. Los swatches del bloque
 * de color (snippet 'pdp-swatches') son <a href="{url del hermano}" class="sw-link">.
 * Por defecto, pulsarlos recarga la página entera. Este script intercepta el clic,
 * trae el producto hermano por fetch y reemplaza SOLO el bloque <product-form> de la
 * PDP (galería + info + variantes + ATC) sin recargar, actualizando la URL.
 *
 * Reutiliza el mismo enfoque de Section Rendering que ya usa el tema en
 * assets/main.js (FilterContainer): fetch -> text -> DOMParser -> swap -> pushState.
 *
 * NOTA (jun 2026): el bloque de swatches se migró de un custom-liquid (clase
 * .swatch-link) al bloque 'color_swatches' / snippet 'pdp-swatches' (clase .sw-link).
 * Este script usa el marcado NUEVO: .sw-link / .sw-active / .sw-excerpt / .sw-track.
 * El propio snippet 'pdp-swatches' gestiona el "+N" y se reinicializa tras el swap
 * (sus <script> inline se re-ejecutan), así que aquí solo interceptamos y swapeamos.
 *
 * Degradación: sin JS, ante error de red o clic con modificador, el <a href>
 * navega de forma normal (recarga completa). Los enlaces siguen siendo reales,
 * así que el SEO no cambia.
 */
(function () {
  'use strict';

  // Evita doble inicialización si el asset se cargara dos veces (p. ej. quickbuy).
  if (window.__ccColorAjaxNav) return;
  window.__ccColorAjaxNav = true;

  var MAIN_SELECTOR = 'product-form.js-product';
  var SWATCH_SELECTOR = '.sw-link';
  var LOADING_CLASS = 'cc-color-loading';
  // El <product-form> de la PDP comparte clase con el clon del quickbuy: hay que
  // excluir cualquier instancia dentro de un modal / vista rápida.
  var MODAL_SELECTOR = '.quickbuy-container, modal-dialog, side-drawer';

  // Cache en memoria de las páginas ya traídas (clave: URL del hermano).
  var pageCache = {};
  var inflight = {};
  var liveRegion = null;

  // ---- Estilo mínimo + región aria-live (inyectados una sola vez) ----
  (function injectAssets() {
    var css =
      'product-form.js-product.' + LOADING_CLASS + '{opacity:.55;transition:opacity .15s;pointer-events:none;}' +
      '.sw-link{transition:opacity .15s;}' +
      '.cc-visually-hidden{position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;}';
    var style = document.createElement('style');
    style.setAttribute('data-cc-color-nav', '');
    style.textContent = css;
    document.head.appendChild(style);

    liveRegion = document.createElement('div');
    liveRegion.className = 'cc-visually-hidden';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('role', 'status');
    document.body.appendChild(liveRegion);
  })();

  // ---- Nodo principal de la PDP (excluyendo modales/quickbuy) ----
  function getMainNode(root) {
    var nodes = (root || document).querySelectorAll(MAIN_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].closest(MODAL_SELECTOR)) return nodes[i];
    }
    return nodes[0] || null;
  }

  function isInModal(el) {
    return !!(el && el.closest(MODAL_SELECTOR));
  }

  // ---- Fetch del HTML del producto hermano (con cache + dedupe) ----
  function fetchPage(url) {
    if (pageCache[url]) return Promise.resolve(pageCache[url]);
    if (inflight[url]) return inflight[url];

    var promise = fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        pageCache[url] = html;
        delete inflight[url];
        return html;
      })
      .catch(function (err) {
        delete inflight[url];
        throw err;
      });

    inflight[url] = promise;
    return promise;
  }

  // ---- Re-ejecuta los <script> de un subárbol recién insertado ----
  // Los scripts insertados vía DOMParser/innerHTML no se ejecutan solos. Clonarlos
  // en nodos <script> nuevos fuerza su ejecución (cubre las IIFE inmediatas del
  // tema: pestañas, y la lógica de "+N" del propio snippet de swatches, que se
  // reinicializa al detectar un nodo nuevo sin data-sw-init).
  function reexecuteScripts(root) {
    var scripts = root.querySelectorAll('script');
    for (var i = 0; i < scripts.length; i++) {
      var old = scripts[i];
      var fresh = document.createElement('script');
      for (var a = 0; a < old.attributes.length; a++) {
        fresh.setAttribute(old.attributes[a].name, old.attributes[a].value);
      }
      fresh.textContent = old.textContent;
      old.parentNode.replaceChild(fresh, old);
    }
  }

  // ---- Accesibilidad: foco + anuncio tras el swap ----
  function announceAndFocus(freshMain, url) {
    var activeSwatch = null;
    var links = freshMain.querySelectorAll(SWATCH_SELECTOR);
    for (var i = 0; i < links.length; i++) {
      if (links[i].getAttribute('href') === url || links[i].classList.contains('sw-active')) {
        activeSwatch = links[i];
        break;
      }
    }
    if (activeSwatch) {
      try { activeSwatch.focus({ preventScroll: true }); } catch (e) { activeSwatch.focus(); }
    }
    if (liveRegion) {
      var heading = freshMain.querySelector('h1');
      var label = (heading && heading.textContent.trim()) ||
        (activeSwatch && (activeSwatch.getAttribute('aria-label') ||
          (activeSwatch.querySelector('img') && activeSwatch.querySelector('img').alt))) || '';
      if (label) liveRegion.textContent = label;
    }
  }

  // ---- Swap del bloque de producto ----
  function performSwap(url, clickedSwatch, push) {
    var live = clickedSwatch ? clickedSwatch.closest(MAIN_SELECTOR) : getMainNode(document);

    if (!live || isInModal(live)) {
      window.location.href = url;
      return;
    }

    live.classList.add(LOADING_CLASS);

    // Activo "optimista": marca ya el swatch pulsado.
    if (clickedSwatch) {
      var group = clickedSwatch.closest('.sw-track');
      if (group) {
        group.querySelectorAll('.sw-link').forEach(function (a) {
          a.classList.remove('sw-active');
        });
      }
      clickedSwatch.classList.add('sw-active');
    }

    fetchPage(url)
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = getMainNode(doc);

        // Reusar el nodo capturado al inicio (no volver a hacer querySelector:
        // evita reemplazar el nodo equivocado si hay un quickbuy en el DOM).
        if (!fresh || !live.isConnected) {
          window.location.href = url;
          return;
        }

        live.replaceWith(fresh);     // re-inicializa los web components hijos
        reexecuteScripts(fresh);     // re-ejecuta las IIFE inline (incl. el "+N" del snippet)
        announceAndFocus(fresh, url);

        if (doc.title) document.title = doc.title;
        if (push) history.pushState({ ccColorNav: true }, '', url);

        // Avisa al resto del tema (carruseles, sticky, bloques) del cambio.
        window.dispatchEvent(new Event('resize'));
        document.dispatchEvent(new CustomEvent('pdp:colorswapped', { detail: { url: url } }));

        // Precalienta los hermanos de la nueva familia (idempotente: la cache
        // evita volver a descargar los ya traídos).
        schedulePrewarm();
      })
      .catch(function () {
        window.location.href = url; // ante cualquier fallo, navegación clásica
      });
  }

  // ---- Detección de clic apto para AJAX ----
  function isPlainLeftClick(e) {
    return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
  }

  document.addEventListener('click', function (e) {
    var link = e.target.closest(SWATCH_SELECTOR);
    if (!link) return;

    // Contador "+N": lo gestiona el propio snippet (revela el resto). No navegar.
    if (link.classList.contains('sw-excerpt')) {
      e.preventDefault();
      return;
    }

    // Swatch ya activo: no hacer nada.
    if (link.classList.contains('sw-active')) {
      e.preventDefault();
      return;
    }

    // Dentro de un quickbuy/modal: dejar la navegación normal (no es la PDP real).
    if (isInModal(link)) return;

    var url = link.getAttribute('href');
    if (!url || link.hasAttribute('data-no-ajax')) return; // dejar navegar normal
    if (!isPlainLeftClick(e)) return;                       // abrir en pestaña, etc.

    e.preventDefault();
    performSwap(url, link, true);
  });

  // ---- Prefetch al pasar el ratón / enfocar (cambio casi instantáneo) ----
  function prefetchFromEvent(e) {
    var link = e.target.closest(SWATCH_SELECTOR);
    if (!link || link.classList.contains('sw-active') || link.classList.contains('sw-excerpt') || isInModal(link)) return;
    var url = link.getAttribute('href');
    if (url && !link.hasAttribute('data-no-ajax')) {
      fetchPage(url).catch(function () {});
    }
  }
  document.addEventListener('mouseover', prefetchFromEvent);
  document.addEventListener('focusin', prefetchFromEvent);

  // ---- Botón atrás / adelante ----
  window.addEventListener('popstate', function () {
    if (location.pathname.indexOf('/products/') === -1) {
      window.location.reload();
      return;
    }
    performSwap(location.href, null, false);
  });

  // ---- Precarga (prewarm) de toda la familia de colores ----
  // Tras cargar la PDP, en tiempo "idle", trae el HTML de los hermanos para que
  // el PRIMER clic en cualquier color ya sea instantáneo (sin esperar al fetch).
  // Escalonado (concurrencia limitada) y desactivado en conexiones lentas / con
  // ahorro de datos, para no competir con la carga inicial de la ficha.
  var prewarmDone = false;

  function connectionIsSlow() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData) return true;
    return /(^|[^a-z])(slow-)?2g($|[^a-z])/.test(c.effectiveType || '');
  }

  function prewarmSiblings() {
    var main = getMainNode(document);
    if (!main) return;
    var links = main.querySelectorAll(SWATCH_SELECTOR);
    var urls = [];
    var seen = {};
    for (var i = 0; i < links.length; i++) {
      var l = links[i];
      if (l.classList.contains('sw-active') || l.classList.contains('sw-excerpt') || l.hasAttribute('data-no-ajax')) continue;
      var u = l.getAttribute('href');
      if (!u || seen[u] || pageCache[u]) continue;
      seen[u] = true;
      urls.push(u);
    }
    if (!urls.length) return;

    var CONCURRENCY = 2;
    var idx = 0;
    function next() {
      if (idx >= urls.length) return;
      fetchPage(urls[idx++]).catch(function () {}).then(next);
    }
    for (var c = 0; c < CONCURRENCY; c++) next();
  }

  function schedulePrewarm() {
    if (prewarmDone || connectionIsSlow()) return;
    prewarmDone = true;
    var run = function () {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(prewarmSiblings, { timeout: 3000 });
      } else {
        setTimeout(prewarmSiblings, 1200);
      }
    };
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }

  // Reabre la posibilidad de prewarm tras un swap (otra familia de colores).
  document.addEventListener('pdp:colorswapped', function () { prewarmDone = false; });

  schedulePrewarm();
})();
